import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "~/api/client";
import { formatEur } from "~/domain/types";
import { dateCompare, naturalCompare } from "~/domain/sorting";
import {
  cancelledInvoiceTotal,
  effectiveInvoiceTotal,
} from "~/domain/reporting";

interface TaxSummary {
  invoiced_cent: number;
  received_cent: number;
  outstanding_cent: number;
  cancelled_cent: number;
  quarters: Array<{
    quarter: number;
    invoiced_cent: number;
    received_cent: number;
  }>;
  invoices: Array<{
    invoice_number: string;
    invoice_date: string;
    customer_name: string;
    total_cent: number;
    status: string;
  }>;
}

export function ReportsPage() {
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("date-desc");

  const { data: summary, isLoading } = useQuery({
    queryKey: ["tax-summary", year],
    queryFn: () => api.get<TaxSummary>(`/reports/tax-summary?year=${year}`),
  });

  const handleDownloadCSV = async () => {
    try {
      const blob = await api.download(
        `/reports/tax-summary?year=${year}&format=csv`,
      );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tax-summary-${year}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    } catch (err) {
      console.error("CSV Download fehlgeschlagen:", err);
    }
  };
  const displayedInvoices = (summary?.invoices ?? [])
    .filter(
      (invoice) =>
        (status === "all" || invoice.status === status) &&
        `${invoice.invoice_number} ${invoice.customer_name} ${invoice.status}`
          .toLocaleLowerCase("de")
          .includes(search.toLocaleLowerCase("de")),
    )
    .sort((left, right) => {
      if (sort === "date-asc")
        return dateCompare(left.invoice_date, right.invoice_date);
      if (sort === "number-asc")
        return naturalCompare(left.invoice_number, right.invoice_number);
      if (sort === "number-desc")
        return naturalCompare(right.invoice_number, left.invoice_number);
      if (sort === "amount-asc") return left.total_cent - right.total_cent;
      if (sort === "amount-desc") return right.total_cent - left.total_cent;
      return dateCompare(right.invoice_date, left.invoice_date);
    });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Berichte</h1>
      </div>

      <div className="mb-6 flex gap-4">
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="rounded-md border border-gray-300 px-4 py-2 text-gray-900 focus:border-blue-500 focus:outline-none"
        >
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <button
          onClick={handleDownloadCSV}
          className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
        >
          CSV exportieren
        </button>
      </div>

      {isLoading ? (
        <div className="text-center text-gray-500">Laden...</div>
      ) : summary ? (
        <div className="space-y-6">
          {/* Zusammenfassung */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <SummaryCard
              label="In Rechnung gestellt"
              value={formatEur(summary.invoiced_cent)}
            />
            <SummaryCard
              label="Erhalten"
              value={formatEur(summary.received_cent)}
            />
            <SummaryCard
              label="Ausstehend"
              value={formatEur(summary.outstanding_cent)}
            />
            <SummaryCard
              label="Storniert"
              value={formatEur(summary.cancelled_cent)}
            />
          </div>

          {/* Quartale */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-xl font-semibold text-gray-900">
              Quartalsübersicht
            </h2>
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Quartal
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      In Rechnung gestellt
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Erhalten
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {summary.quarters.map((q) => (
                    <tr key={q.quarter} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        Q{q.quarter} {year}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatEur(q.invoiced_cent)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatEur(q.received_cent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detaillierte Rechnungen */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-xl font-semibold text-gray-900">
              Rechnungsdetails
            </h2>
            <div className="mb-4 flex flex-wrap gap-3">
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nummer, Kunde oder Status suchen…" className="min-w-64 flex-1 rounded-md border px-3 py-2" />
              <select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded-md border px-3 py-2">
                <option value="all">Alle Status</option>
                {[...new Set((summary?.invoices ?? []).map((invoice) => invoice.status))].sort(naturalCompare).map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
              <select value={sort} onChange={(event) => setSort(event.target.value)} className="rounded-md border px-3 py-2">
                <option value="date-desc">Datum: neueste zuerst</option>
                <option value="date-asc">Datum: älteste zuerst</option>
                <option value="number-asc">Nummer aufsteigend</option>
                <option value="number-desc">Nummer absteigend</option>
                <option value="amount-desc">Betrag absteigend</option>
                <option value="amount-asc">Betrag aufsteigend</option>
              </select>
            </div>
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Rechnungsnummer
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Datum
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Kunde
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Betrag
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {displayedInvoices.map((inv) => (
                    <tr key={inv.invoice_number} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {inv.invoice_number}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(inv.invoice_date).toLocaleDateString("de-DE")}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {inv.customer_name}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {formatEur(inv.total_cent)}
                      </td>
                      <td className="px-6 py-4 text-sm">{inv.status}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 bg-gray-50">
                  <tr>
                    <td colSpan={3} className="px-6 py-4 text-right font-bold">
                      Gesamt (ohne Stornierungen)
                    </td>
                    <td className="px-6 py-4 font-bold">
                      {formatEur(effectiveInvoiceTotal(displayedInvoices))}
                    </td>
                    <td />
                  </tr>
                  {cancelledInvoiceTotal(displayedInvoices) > 0 && (
                    <tr>
                      <td colSpan={3} className="px-6 pb-4 text-right text-sm text-gray-600">
                        Storniert / abgeschrieben
                      </td>
                      <td className="px-6 pb-4 text-sm text-gray-600">
                        {formatEur(cancelledInvoiceTotal(displayedInvoices))}
                      </td>
                      <td />
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-sm text-gray-600">{label}</p>
      <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
